// BL-177 Step 2d: short links for staff text alerts, https://<staff app>/c/<code>.
// The code is 12 random base62 characters made and stored by the database (conversation_short_links); the link itself
// carries nothing else. Opening it needs a normal sign-in, then resolve_conversation_short_link(p_code) answers with the
// conversation id only for someone at that company who can already see that conversation.
//
// Import-free on purpose: the browser route (/c/:code) and the Deno dispatcher (notification-channel-dispatch) share it.

export const SHORT_LINK_CODE = /^[0-9A-Za-z]{12}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const SHORT_LINK_NOT_AVAILABLE = "This link isn't available. It may have expired, or you may not have access to that conversation.";
export const SHORT_LINK_FAILED = "We couldn't open this link. Check your connection and try again.";

export function isShortLinkCode(value) {
    return typeof value === 'string' && SHORT_LINK_CODE.test(value);
}

/** https://app.therideatl.com/c/Ab3dE5gH7jK9, or '' when the origin isn't https or the code isn't a short link code. */
export function shortLinkUrl(origin, code) {
    if (!isShortLinkCode(code)) return '';
    try {
        const base = new URL(String(origin));
        return base.protocol === 'https:' ? `${base.origin}/c/${code}` : '';
    } catch {
        return '';
    }
}

/** The code in a "/c/<code>" address (whatever it is, checked later), or null when the address isn't a short link. */
export function shortLinkCodeFromPath(pathname) {
    const match = /^\/c\/([^/?#]*)\/?$/.exec(String(pathname ?? ''));
    if (!match) return null;
    try {
        return decodeURIComponent(match[1]);
    } catch {
        return '';
    }
}

/** Where a resolved link opens: the conversation in Communications. '' for anything that isn't a conversation id. */
export function conversationPath(sessionId) {
    return typeof sessionId === 'string' && UUID.test(sessionId) ? `/communications?session=${sessionId.toLowerCase()}` : '';
}

/** The conversation id inside a "/communications?session=<id>" link, or ''. */
export function sessionIdFromConversationPath(path) {
    const match = /^\/communications\?session=([0-9a-f-]{36})$/i.exec(String(path ?? '').trim());
    return match && UUID.test(match[1]) ? match[1].toLowerCase() : '';
}

/**
 * What the person sees when a link doesn't open. Every "no" from the database is the same 42501 answer (another company,
 * expired, made up, no access), so they all read the same; anything else is a connection problem worth retrying.
 */
export function shortLinkErrorMessage(error) {
    const code = String(error?.code ?? '');
    return code === '42501' || code === 'PGRST301' ? SHORT_LINK_NOT_AVAILABLE : SHORT_LINK_FAILED;
}
